import { useCallback, useState } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Label, ListGroup, ListGroupItem, Badge, Row, Col } from "reactstrap";
import { API_BASE_URL, EMPTY_LIST } from "src/common/constants";
import UserUtils from "src/services/utils/UserUtils";
import axios from "axios";
import { toast } from "react-toastify";
import AsyncReactSelect from "src/components/AsyncReactSelect";
import ThirdpartyManagementService from "src/apis/ThirdpartyManagementService";
import { 
    hasUserAccessToModule
} from "src/helpers/module_helper";

const TPDDThirdPartiesList = ({
    t,
    assignedAnalyst,
    user,
    reportIsDeleted,
    reportCaseId,
    defaultValues = [],
    token,
    modules
}) => {
    const thirdpartyManagementService = ThirdpartyManagementService.getInstance();

    const userUtils = new UserUtils();
    const [expandedThirdparties, setExpandedThirdparties] = useState(false);
    const [selectedThirdparties, setSelectedThirdparties] = useState({
        isLoading: false,
        items: defaultValues.map((item) => {
            return {
                value: item.id,
                label: item.name
            }
        })
    });

    const apiBaseUrl = modules.active === 'cm' ? `${API_BASE_URL}/cm` : API_BASE_URL;

    const userType = userUtils.isAnalystAdmin(user) ? 'admin' : (
        userUtils.isAnalyst(user) ? 'analyst' : 'else'
    );

    const fetchSuppliersOptions = useCallback(async (params) => {
        const response = await thirdpartyManagementService.fetchSuppliersList(params, modules.active);

        const options = response.suppliers.map((entity) => {
            return {
                value: entity.id,
                label: entity.name
            }
        });

        return {
            options: options,
            pageIndex: response.pageIndex,
            pageSize: response.pageSize,
            totalCount: response.itemsCount
        }
    }, []);

    const handleAddThirdparty = (id) => {
        setSelectedThirdparties((currentState) => {
            return {
                ...currentState,
                isLoading: true
            }
        });

        axios.post(
            `${apiBaseUrl}/report_case/add_third_party`,
            {
                reportCaseId: reportCaseId,
                thirdPartyId: id
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        ).then(({ data }) => {
            setSelectedThirdparties((currentState) => {
                return {
                    ...currentState,
                    isLoading: false,
                    items: (data?.data?.third_parties || []).map((thirdparty) => {
                        return {
                            value: thirdparty.id,
                            label: thirdparty.name
                        }
                    })
                }
            });

            toast(t("The selected third party has been successfully added to the list."), {
                type: "success",
            });
        }).catch(() => {
            setSelectedThirdparties((currentState) => {
                return {
                    ...currentState,
                    isLoading: false
                }
            });

            toast(t("An error occurred while attempting to add the third party to the list."), {
                type: "error",
            });
        })
    }

    const handleRemoveThirdparty = (id) => {
        setSelectedThirdparties((currentState) => {
            return {
                ...currentState,
                isLoading: true
            }
        });

        axios.post(
            `${apiBaseUrl}/report_case/remove_third_party`,
            {
                reportCaseId: reportCaseId,
                thirdPartyId: id
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        ).then(({ data }) => {
            setSelectedThirdparties((currentState) => {
                return {
                    ...currentState,
                    isLoading: false,
                    items: (data?.data?.third_parties || []).map((thirdparty) => {
                        return {
                            value: thirdparty.id,
                            label: thirdparty.name
                        }
                    })
                }
            });

            toast(t("The third party has been successfully removed from the list."), {
                type: "success",
            });
        }).catch(() => {
            setSelectedThirdparties((currentState) => {
                return {
                    ...currentState,
                    isLoading: false
                }
            });

            toast(t("An error occurred while attempting to remove the third party from the list."), {
                type: "error",
            });
        })
    }

    if(!hasUserAccessToModule('tp') || userType === 'else' || (
        userType === 'analyst' && user.id !== assignedAnalyst.user?.value
    )) return null;

    return (
        <Col sm="6" md="6" lg="6" className="mb-3">
            <div className="module__tpdd-thirdparties-selection">
                <Label className="form-label text-dark d-flex justify-content-between">
                    {t("Choose related third parties")}:
                    <span
                        onClick={() => setExpandedThirdparties(!expandedThirdparties)}
                        className="expand-btn btn-primary"
                    >
                        {expandedThirdparties
                        ? t("Collapse list")
                        : t("Expand list")}
                    </span>
                </Label>

                <div className="items-container">
                    <div>   
                        {selectedThirdparties.items.length > 0 ? (
                            <ListGroup
                                type="inline"
                                style={{
                                    maxHeight: expandedThirdparties ? "171px" : "51px"
                                }}
                            >
                                {selectedThirdparties.items.map((thirdparty, index) => {
                                    return (
                                        <ListGroupItem key={index}>
                                            {thirdparty.label}
                                            {(userType === 'admin' || !assignedAnalyst.isReadOnly) && (
                                                <Badge
                                                    className="delete-btn text-dark pt-2"
                                                    color="light"
                                                    onClick={() => {
                                                        handleRemoveThirdparty(thirdparty.value);
                                                    }}
                                                >
                                                X
                                                </Badge>
                                            )}
                                        </ListGroupItem>
                                    )
                                })}   
                            </ListGroup>
                        ) : (
                            <p
                                className="empty-list-message"
                                style={{
                                    maxHeight: expandedThirdparties ? "163px" : "43px",
                                }}
                            >
                                {t(EMPTY_LIST)}
                            </p>
                        )}

                        {!reportIsDeleted && (
                            <Row className="mt-2">
                                <Col sm='12'>
                                    <AsyncReactSelect 
                                        loadOptions={fetchSuppliersOptions}
                                        settings={{
                                            queryKey: 'name',
                                            loadOptionsErrorMessage: 'Faild to fetch'
                                        }}
                                        value={[]}
                                        onChange={(e) => {
                                            handleAddThirdparty(e.value)
                                        }}
                                        placeholder={t("Select a third party")}
                                    />
                                </Col>
                            </Row>
                        )}
                    </div>
                </div>
            </div>
        </Col>
    )
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Modules } = state;
  
    return {
        token,
        user,
        modules: Modules
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps
    )(
        TPDDThirdPartiesList
    )
);